

/** Every crystal size, smallest first. */
export const EMPYREAL_CRYSTAL_SIZES = ['splinter', 'fragment', 'complete'] as const;

export type EmpyrealCrystalSize = typeof EMPYREAL_CRYSTAL_SIZES[number];

/** Every crystal type. */
export const EMPYREAL_CRYSTAL_TYPES = ['resonance', 'vitality', 'insight', 'warding', 'fury'] as const;

export type EmpyrealCrystalType = typeof EMPYREAL_CRYSTAL_TYPES[number];

/**
 * What makes crystals the same kind: the recipe that coalesced them (with its
 * type and size). Recipes that share a type and size have different effects,
 * so only crystals from the same recipe stack together.
 */
export interface EmpyrealCrystalKind {
    readonly recipeId: string;
    readonly crystalType: EmpyrealCrystalType;
    readonly size: EmpyrealCrystalSize;
}

export interface EmpyrealCrystalStack extends EmpyrealCrystalKind {
    readonly quantity: number;
}

/**
 * A stack of Empyreal Crystals held in the carried inventory, occupying one
 * carried slot like any other item. Crystals are run-bound: they exist only
 * during an active Crucible run and are never banked or persisted to the
 * account (see docs/empyreal-crystal-mechanics.md).
 */
export interface EmpyrealCrystalItem extends EmpyrealCrystalStack {
    /** Unique id for this stack, used to target it (e.g. by an action-bar hotkey). */
    readonly id: string;
    readonly kind: 'empyreal-crystal';
}

/**
 * One crystal a player coalesced on the gathering page, before entering, from
 * their banked essence (by substitution; see docs/empyreal-crystal-mechanics.md).
 * A carried slot's starting stack is every pick aimed at it.
 */
export interface StartingCrystalPick {
    /** The standard recipe it was coalesced from. */
    readonly recipeId: string;
    /** Carried slot it goes into. */
    readonly slotIndex: number;
}

/**
 * What an active crystal effect changes, as additive fractions (0.25 is +25%,
 * -0.1 is -10%). The modifiers of every effect active on a character sum.
 */
export interface CrystalEffectModifiers {
    /** Outgoing damage. */
    readonly outgoingDamage?: number;
    /** Damage taken; negative is damage reduction. */
    readonly damageTaken?: number;
    /** Avoidance (dodge or resistance), the chance an attack misses. */
    readonly defense?: number;
    /** Essence restored by healing, such as a crystal's restore. */
    readonly healingReceived?: number;
    /** Praxis earned from skill use. */
    readonly praxisGain?: number;
}

/**
 * An explosion a crystal effect sets off: every other character within
 * `radiusTiles` of its center takes a share of the triggering damage.
 */
export interface CrystalExplosion {
    readonly radiusTiles: number;
    /** Share of the triggering damage each enemy caught in it takes. */
    readonly damageFraction: number;
    /**
     * Share of the triggering damage each of the user's allies caught in it
     * takes; 0 spares them. Pure Fury crystals hurt allies (less than
     * enemies); a Fury recipe that mixes in another essence (beyond the
     * Resonance base) spares them.
     */
    readonly allyDamageFraction: number;
}

/** A pulse that goes off around a crystal's user on a timer. */
export interface CrystalPulse extends CrystalExplosion {
    readonly intervalMs: number;
}

/** What a crystal effect makes happen, beyond its summed modifiers. On the user only. */
export interface CrystalEffectBehaviors {
    /** Each hit the user lands explodes around its target, for a share of the hit's damage. */
    readonly explodeOnHit?: CrystalExplosion;
    /** A pulse around the user every `intervalMs`, for a share of their average weapon damage. */
    readonly pulse?: CrystalPulse;
    /** Enemies that can see the user target them first, and notice them from farther away. */
    readonly drawAggression?: { readonly sightRangeMultiplier: number };
    /**
     * Weak points are revealed: each weapon hit the user lands has `chance`
     * (0-1) to strike one for bonus damage. Weak points exist only while a
     * crystal reveals them.
     */
    readonly revealWeakPoints?: { readonly chance: number };
}

/** Who a crystal effect is on: the crystal's user, or the rest of their party. */
export type CrystalEffectScope = 'self' | 'allies';

/**
 * What using a crystal does, set by its recipe. A crystal can restore essence
 * at once and start timed modifiers on its user and the rest of the party.
 */
export interface CrystalEffectDefinition {
    /** How long the timed modifiers last. */
    readonly durationMs: number;
    /** Timed modifiers on the user. */
    readonly self?: CrystalEffectModifiers;
    /** Timed behaviors on the user, such as explosions. */
    readonly behaviors?: CrystalEffectBehaviors;
    /** Timed modifiers on every other party member in the run. */
    readonly allies?: CrystalEffectModifiers;
    /** Share of the user's max essence pool restored at once. */
    readonly restoreFraction?: number;
    /** Share of each other party member's max essence pool restored at once. */
    readonly allyRestoreFraction?: number;
    /**
     * Reveals map areas to the user at once, for the rest of the run: those
     * within `radiusTiles` of where they stand, or the whole Crucible when
     * `radiusTiles` is left out.
     */
    readonly revealMap?: { readonly radiusTiles?: number };
}

export const EMPYREAL_CRYSTAL_STACK_LIMITS: Readonly<Record<EmpyrealCrystalSize, number>> = {
    complete: 1,
    fragment: 2,
    splinter: 6,
};
